import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';
import { Crosshair, MagnifyingGlass, X } from '@phosphor-icons/react';
import {
  geocodeAutocomplete,
  geocodePlaceDetails,
  geocodeReverse,
  newGeocodeSessionToken,
  type GeocodeKinds,
  type GeocodePlace,
  type GeocodePrediction,
} from '../api/geocode';
import { ApiError } from '../api/client';

interface Props {
  // Pre-fill text for the input — used to display the formatted address of
  // a previously-picked place when the user comes back to this step.
  initialText?: string;
  // Bias suggestions to a region (ISO 3166-1 alpha-2 uppercase). When the
  // browser locale gives us a region we pre-bias; otherwise we pass empty.
  regionBias: string;
  // Bias result language (ISO 639-1 lowercase). Affects how Google formats
  // names in suggestions, not which countries are returned.
  language: string;
  // Controls what types of places the autocomplete returns. Defaults to ''
  // (addresses only). Pass 'any' when the user is picking a venue (sports
  // hall, school, park) rather than a home/office address.
  kinds?: GeocodeKinds;
  // Fired once the user picks a prediction and we've fetched its details.
  // The parent fans the structured fields onto the draft.
  onPicked: (place: GeocodePlace) => void;
  // Fired after a successful "find me" reverse-geocode. Same payload as
  // onPicked, but the parent typically wants to switch to manual-entry
  // mode after this — geolocation often only resolves to city accuracy
  // on desktop, so the user needs to see all fields to fill the gaps.
  onLocated: (place: GeocodePlace) => void;
  // Fired when the geocoder returns 503 — usually means the server has no
  // GOOGLE_PLACES_API_KEY. The parent should swap to manual entry.
  onUnavailable: () => void;
  // Fired when the user hits the clear button so the parent can wipe any
  // address fields it had auto-filled from the previous pick.
  onClear: () => void;
  // Helper line under the input. Defaults to the onboarding copy that
  // promises to fill country/city/office details below — callers reusing
  // this component outside onboarding should pass their own (or `null` to
  // hide it entirely).
  hint?: ReactNode;
}

// Keystrokes are debounced before hitting the server. 300ms is the usual
// sweet spot — fast enough to feel responsive, slow enough that typing a
// 10-character query doesn't fire 10 backend calls.
const DEBOUNCE_MS = 300;
const MIN_QUERY_LEN = 3;

// AddressAutocomplete is a lightweight combobox that talks to our
// /api/v1/geocode/* proxy. Click a suggestion -> the parent gets a
// structured GeocodePlace via onPicked.
//
// Deliberately simple: no keyboard navigation between predictions yet, no
// virtual list. We can layer those on if usage warrants.
export function AddressAutocomplete({
  initialText = '',
  regionBias,
  language,
  kinds = '',
  onPicked,
  onLocated,
  onUnavailable,
  onClear,
  hint,
}: Props) {
  const auth = useAuth();
  const { t } = useTranslation('common');
  const inputId = useId();
  const listboxId = useId();

  const [query, setQuery] = useState(initialText);
  const [predictions, setPredictions] = useState<GeocodePrediction[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [pickingId, setPickingId] = useState<string | null>(null);
  const [locating, setLocating] = useState(false);
  // Session token shared between Autocomplete + Details so Google bills
  // them as a single session (cheaper). Reset after each successful pick.
  const sessionTokenRef = useRef<string>(newGeocodeSessionToken());

  const containerRef = useRef<HTMLDivElement>(null);

  // `hint === undefined` → use the onboarding default; an explicit `null`
  // (or empty string) lets a caller hide the hint without rendering an
  // empty <p>.
  const resolvedHint = hint === undefined ? t('onboarding.orgInfo.address.hint') : hint;

  // Click outside dismisses the predictions panel without clearing input.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Debounced fetch. Skips when the query is too short — Google charges
  // per session, not per keystroke, but a 1-character query produces
  // garbage suggestions anyway.
  useEffect(() => {
    if (query.trim().length < MIN_QUERY_LEN) {
      setPredictions([]);
      setErrorKey(null);
      return;
    }
    const handle = setTimeout(() => {
      void fetchPredictions(query);
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, regionBias, language, kinds]);

  async function fetchPredictions(q: string) {
    const token = auth.user?.access_token ?? null;
    setLoading(true);
    setErrorKey(null);
    try {
      const preds = await geocodeAutocomplete(
        token,
        q,
        sessionTokenRef.current,
        language,
        regionBias,
        kinds,
      );
      setPredictions(preds);
      setOpen(true);
    } catch (err) {
      setPredictions([]);
      if (err instanceof ApiError && err.code === 'geocode_unavailable') {
        onUnavailable();
        return;
      }
      setErrorKey(err instanceof ApiError ? err.code : 'unknown');
      setOpen(true);
    } finally {
      setLoading(false);
    }
  }

  async function handlePick(prediction: GeocodePrediction) {
    const token = auth.user?.access_token ?? null;
    setPickingId(prediction.place_id);
    setErrorKey(null);
    try {
      const place = await geocodePlaceDetails(
        token,
        prediction.place_id,
        sessionTokenRef.current,
        language,
      );
      // Reset session for the next search — Google considers the session
      // "consumed" once we've called Details.
      sessionTokenRef.current = newGeocodeSessionToken();
      setQuery(place.formatted_address);
      setPredictions([]);
      setOpen(false);
      onPicked(place);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'geocode_unavailable') {
        onUnavailable();
        return;
      }
      setErrorKey(err instanceof ApiError ? err.code : 'unknown');
    } finally {
      setPickingId(null);
    }
  }

  // handleLocate asks the browser for the user's current coordinates and
  // resolves them server-side. Geolocation requires a user gesture (this
  // click counts) and a secure context (always true in prod; localhost in
  // dev). On desktop the result is usually only accurate to ~city level —
  // the parent compensates by switching to manual-entry mode so all fields
  // are visible and editable.
  function handleLocate() {
    if (!('geolocation' in navigator)) {
      setErrorKey('location_unavailable');
      setOpen(true);
      return;
    }
    setLocating(true);
    setErrorKey(null);
    setOpen(false);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const token = auth.user?.access_token ?? null;
        geocodeReverse(token, pos.coords.latitude, pos.coords.longitude, language)
          .then((place) => {
            sessionTokenRef.current = newGeocodeSessionToken();
            setQuery(place.formatted_address);
            setPredictions([]);
            onLocated(place);
          })
          .catch((err: unknown) => {
            if (err instanceof ApiError && err.code === 'geocode_unavailable') {
              onUnavailable();
              return;
            }
            // 400 from the server when reverse geocode returns ZERO_RESULTS.
            const code =
              err instanceof ApiError && err.code === 'invalid_input'
                ? 'location_no_result'
                : err instanceof ApiError
                  ? err.code
                  : 'unknown';
            setErrorKey(code);
            setOpen(true);
          })
          .finally(() => setLocating(false));
      },
      (err) => {
        setLocating(false);
        // PERMISSION_DENIED = 1, POSITION_UNAVAILABLE = 2, TIMEOUT = 3.
        const code =
          err.code === 1 ? 'location_denied'
          : err.code === 3 ? 'location_timeout'
          : 'location_unavailable';
        setErrorKey(code);
        setOpen(true);
      },
      // 10s is generous for WiFi triangulation; phone GPS is usually <2s.
      // maximumAge=60s lets a recent fix satisfy a re-click without a
      // fresh sensor read.
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 60_000 },
    );
  }

  function handleClear() {
    setQuery('');
    setPredictions([]);
    setErrorKey(null);
    setOpen(false);
    sessionTokenRef.current = newGeocodeSessionToken();
    onClear();
  }

  return (
    <div className="address-autocomplete" ref={containerRef}>
      <label htmlFor={inputId}>{t('onboarding.orgInfo.address.label')}</label>
      <div className="address-autocomplete__row">
        <div className="address-autocomplete__input-wrap">
          <MagnifyingGlass className="address-autocomplete__leading-icon" aria-hidden />
          <input
            id={inputId}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => predictions.length > 0 && setOpen(true)}
            placeholder={t('onboarding.orgInfo.address.placeholder')}
            autoComplete="off"
            spellCheck={false}
            role="combobox"
            aria-expanded={open}
            aria-controls={listboxId}
            aria-autocomplete="list"
          />
          {query !== '' && (
            <button
              type="button"
              className="address-autocomplete__clear"
              onClick={handleClear}
              aria-label={t('onboarding.orgInfo.address.clear')}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <button
          type="button"
          className="btn btn--primary address-autocomplete__locate"
          onClick={handleLocate}
          disabled={locating}
        >
          <Crosshair className="h-4 w-4" weight={locating ? 'fill' : 'bold'} aria-hidden />
          <span>
            {locating
              ? t('onboarding.orgInfo.address.findingMe')
              : t('onboarding.orgInfo.address.findMe')}
          </span>
        </button>
      </div>
      {resolvedHint ? <p className="form__hint">{resolvedHint}</p> : null}

      {open && (predictions.length > 0 || loading || locating || errorKey) && (
        <div className="address-autocomplete__panel" role="listbox" id={listboxId}>
          {locating && (
            <div className="address-autocomplete__status">
              {t('onboarding.orgInfo.address.findingMe')}
            </div>
          )}
          {loading && !locating && predictions.length === 0 && (
            <div className="address-autocomplete__status">
              {t('onboarding.orgInfo.address.loading')}
            </div>
          )}
          {!loading && errorKey && (
            <div className="address-autocomplete__status address-autocomplete__status--error">
              {t(`onboarding.orgInfo.address.error.${errorKey}`, {
                defaultValue: t('onboarding.orgInfo.address.error.unknown'),
              })}
            </div>
          )}
          {predictions.map((p) => (
            <button
              key={p.place_id}
              type="button"
              role="option"
              aria-selected={false}
              className="address-autocomplete__option"
              onClick={() => void handlePick(p)}
              disabled={pickingId !== null}
            >
              <span className="address-autocomplete__option-main">{p.main_text}</span>
              <span className="address-autocomplete__option-secondary">{p.secondary_text}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
